<template>
    <div class="onboarding-root">
        <header class="onboarding-logo">
            <img src="/img/ashore-logo.svg" alt="Ashore" width="150">
        </header>
        <auth-shell :show-panel="currentStep === 4" wide class="onboarding-page">
        <onboarding-step-indicator v-if="currentStep < 4" :step="currentStep" :total-steps="3" />
        <Screen1
            v-if="currentStep === 1"
            :currentStep="currentStep"
            :handleNextStep="handleNextStep"
            :featuresIncludes="featuresIncludes"
            :featuresCanUnlock="featuresCanUnlock"
            />
        <Screen2 v-if="currentStep === 2" :currentStep="currentStep" :handleNextStep="handleNextStep"
            :handlePrevStep="handlePrevStep" />
        <Screen3 v-if="currentStep === 3" :currentStep.sync="currentStep" :handleNextStep="handleNextStep"
            :handlePrevStep="handlePrevStep" :checklist-items="checklistItems" @checklist-saved="handleChecklistSaved" />
        <Screen4 v-if="currentStep === 4" :featuresCanUnlock="featuresCanUnlock" :currentStep="currentStep" />

        <template #panel>
            <div class="video-container">
                <!-- Wistia Video Embed -->
                <div class="video-wrapper">
                    <div class="wistia_responsive_wrapper">
                        <div class="wistia_embed wistia_async_pvry2omp6m seo=true videoFoam=true">
                            <div class="wistia_swatch">
                                <img src="https://fast.wistia.com/embed/medias/pvry2omp6m/swatch"
                                    alt=""
                                    aria-hidden="true" onload="this.parentNode.style.opacity=1;" />
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Quote Section -->
                <div class="quote-section">
                    <img src="/img/quotes-green.svg" alt="Quote" class="quote-icon" />
                    <div class="text-start">
                        <div class="quote-text">
                            I can't imagine not having Ashore now!
                        </div>
                        <div class="quote-author">
                            Jesse, Alpine Apparel Company
                        </div>
                    </div>
                </div>
            </div>
        </template>
        </auth-shell>
    </div>
</template>

<script>
import Screen1 from '../components/onboarding/screen1.vue';
import Screen2 from '../components/onboarding/screen2.vue';
import Screen3 from '../components/onboarding/screen3.vue';
import Screen4 from '../components/onboarding/screen4.vue';
import AuthShell from '../components/auth/auth-shell.vue';
import OnboardingStepIndicator from '../components/onboarding/onboarding-step-indicator.vue';

const DEFAULT_CHECKLIST_ITEMS = [
    { name: "I have checked for spelling and errors" },
    { name: "I assume liability for any mistakes" },
    { name: "I understand that upon my approval, this is now going to production" }
];

const FEATURES_INCLUDES = [
    'PDF, image and web proofs',
    'Real-time creative collaboration',
    'Proof approval checklists',
];

const FEATURES_CAN_UNLOCK = [
    'Full workflow automation',
    'Customizable email templates',
    'Audio and video proofs',
    'More files and unlimited storage limits',
    'Full review screen white-labeling',
    'Watermark PDFs',
    'Unlimited custom fields',
    'Job Management',
    'Artwork Intake Forms',
    'Zapier integration',
    'Adobe plugins',
    'API access',
];

export default {
    name: "OnboardingScreen",
    components: {
        Screen1,
        Screen2,
        Screen3,
        Screen4,
        AuthShell,
        OnboardingStepIndicator
    },
    data() {
        return {
            currentStep: 1,
            checklistItems: [],
            defaultChecklistItems: DEFAULT_CHECKLIST_ITEMS,
            featuresIncludes: FEATURES_INCLUDES,
            featuresCanUnlock: FEATURES_CAN_UNLOCK,
            wistiaLoaded: false,
        };
    },
    created() {
        this.initializeOnboarding();
    },
    async mounted() {
        await this.loadChecklist();
    },
    watch: {
        currentStep: {
            immediate: true,
            handler(step) {
                // Wistia is only used by step 4's video pane; defer the
                // third-party scripts until the user actually gets there
                if (step === 4 && !this.wistiaLoaded) {
                    this.wistiaLoaded = true;
                    this.loadWistiaScripts();
                }
            }
        }
    },
    methods: {
        initializeOnboarding() {
            this.currentStep = this.$A.Auth.onboardingStep ?? 1;
        },
        async loadWistiaScripts() {
            const scripts = [
                {
                    src: 'https://fast.wistia.com/embed/medias/pvry2omp6m.jsonp',
                    async: true
                },
                {
                    src: 'https://fast.wistia.com/assets/external/E-v1.js',
                    async: true
                }
            ];

            scripts.forEach(script => {
                const scriptElement = document.createElement('script');
                Object.assign(scriptElement, script);
                document.head.appendChild(scriptElement);
            });
        },
        async handleNextStep() {
            if (this.currentStep >= 4) {
                return this.currentStep = 4;
            }
            this.currentStep++;
            const onboardingStep = _.get(this, '$A.Auth.onboardingStep', 1);
            if(onboardingStep >= this.currentStep) return;
            try {
                const currentUser = _.get(this, '$A.Auth', null);
                if (!currentUser) return;
                const response = await this.$A.UserService.UpdateUser(currentUser.id, { onboardingStep: this.currentStep, email: currentUser.email }, false)
                response && this.$A.UpdateUserObjectAndBustCache(response);
            } catch (error) {
                this.$A.AlertUser("Failed to update onboarding step. Please try again.", "error");
                this.currentStep--;
            }
        },
        async handlePrevStep() {
            if (this.currentStep <= 1) {
                return this.currentStep = 1;
            }
            this.currentStep--;
        },
        handleChecklistSaved() {
            this.currentStep = 4;
        },
        async loadChecklist() {
            try {
                const checklists = await this.$A.ProofChecklistService.GetAllProofChecklistsItems();
                if (Array.isArray(checklists) && checklists.length > 0) {
                    const [firstChecklist] = checklists;
                    this.checklistItems = {...firstChecklist,items: [...firstChecklist.items, ...this.defaultChecklistItems],};
                } else {
                    this.$A.AlertUser("No checklists found.", "warning");
                }
            } catch (error) {
                this.$A.AlertUser("There was an issue with the API. Please try logging in again.", "warning");
            }
        },
    },
};
</script>

<style scoped>
.onboarding-root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: #f4f5f7;
}

.onboarding-logo {
    padding: 20px 32px 0;
}

/* the shell fills the space below the logo instead of the full viewport */
.onboarding-page {
    min-height: 0;
    flex: 1;
}

.video-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 48px;
}

.video-wrapper {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 56.25%;
    margin-bottom: 0;
}

.wistia_responsive_wrapper {
    height: 100%;
    left: 0;
    position: absolute;
    width: 100%;
}

.wistia_embed {
    height: 100%;
    position: relative;
    width: 100%;
    border-radius: 12px;
    overflow: hidden;
}

.wistia_swatch {
    height: 100%;
    left: 0;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    transition: opacity 200ms;
    width: 100%;
}

.wistia_swatch img {
    filter: blur(5px);
    height: 100%;
    object-fit: contain;
    width: 100%;
}

.quote-section {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
}

.quote-icon {
    width: 28px;
    height: auto;
    flex-shrink: 0;
}

.quote-text {
    font-size: 22px;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.35;
    color: #111827;
}

.quote-author {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-top: 8px;
    color: #019256;
}
</style>

<!-- Shared classes used inside Screen1-4's own templates, which this
     component's scoped styles can't reach — unscoped and namespaced
     under .onboarding-page (same pattern as auth-shell) -->
<style>
/* gray page body; the white surfaces are the .onboarding-card blocks */
.onboarding-page.auth-shell {
    background: #f4f5f7;
}

.onboarding-page .auth-card {
    background: transparent;
}

.onboarding-page .auth-card-wide {
    max-width: 680px;
}

.onboarding-page .auth-btn-primary,
.onboarding-page .auth-btn-ghost {
    width: auto;
}

.onboarding-page .onboarding-title {
    font-size: 26px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #111827;
    margin: 0 0 10px;
}

.onboarding-page .onboarding-desc {
    font-size: 14px;
    line-height: 1.6;
    color: #6b7280;
    margin-bottom: 20px;
}

.onboarding-page .onboarding-card {
    background: #fff;
    border: 1px solid rgba(15, 23, 42, 0.07);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
    margin-bottom: 24px;
}

.onboarding-page .onboarding-card-section {
    padding: 20px 24px;
}

.onboarding-page .onboarding-card-section + .onboarding-card-section {
    border-top: 1px solid rgba(15, 23, 42, 0.07);
}

.onboarding-page .onboarding-card-heading {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 12px;
}

.onboarding-page .feature-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;
}

.onboarding-page .feature-list li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #374151;
}

.onboarding-page .onboarding-actions {
    display: inline-flex;
    gap: 12px;
    flex-wrap: wrap;
}
</style>
